/**
 * The game's songs (OD-022; the owner's values 16.9.2026). Files go in apps/client/public/audio/music/<file>: AAC-LC in an
 * .m4a container, 48 kHz (nginx types .m4a as audio/x-m4a; a bare .aac has no type there and would never load).
 *
 * Sample positions are on the DECODED asset, not on the master: the intro runs 0 → loopStartSample, the loop repeats
 * [loopStartSample, loopEndSample), exitSamples are the other musically allowed places to leave it (empty = the loop's own
 * end is the only exit), and outroStartSample is where the outro begins (the loop's end when the tail simply follows).
 * The director clamps every marker to the decoded buffer's length.
 *
 * pool: where the song BELONGS, as a list of tags the owner chose (5.10.2026): all · friends · meet · flirt · date · k18.
 * A room draws every song tagged 'all', every song tagged with its own kind, and — when the room is in K-18 mode — every
 * song tagged 'k18'. So K-18 is not a room, it is a second axis: "esim. Friends K-18 moodissa saa K-18 biisit mukaan
 * arvontapooliin" (Marko). A song plays ONLY where it is tagged, so nothing adult can wander into a room that did not
 * ask for it. It was one word, 'flirt' or 'general', and those still parse as what they meant. At most three of the ten
 * are K-18 songs (OD-022).
 * spotify: the song's official page, opened from the 34A music card.
 *
 * The songs arrive as .aac but are MP4/M4A containers, and nginx types .m4a while a bare .aac has no type there and
 * would never load — so each is published as .m4a.
 */
import REGISTRY from '../../public/audio/music/tracks.json';

export type Track = {
  id: string; file: string; title: string; artist: string; spotify: string | null;
  sampleRate: number; loopStartSample: number; loopEndSample: number; exitSamples: number[]; outroStartSample: number;
  pool: MusicTag[];
};

/** the owner's own tags, 5.10.2026: "Vaihtoehdot ovat All, Flirt, Meet, Friends, Date, K-18". Date is not a RoomType of
 *  its own (it is a Friends room with dateMode) and K-18 is not a room at all — songs are tagged by what the room IS to
 *  a player, not by its protocol shape. */
export type MusicTag = 'all' | 'friends' | 'meet' | 'flirt' | 'date' | 'k18';
/** the room a song is being picked for: its kind, and whether it is running K-18 */
export type MusicRoom = { kind: 'friends' | 'meet' | 'flirt' | 'date'; k18: boolean };
const TAGS: MusicTag[] = ['all', 'friends', 'meet', 'flirt', 'date', 'k18'];
/** 'flirt' and 'general' were the two words before the tags; they keep meaning what they meant */
const poolOf = (p: unknown): MusicTag[] =>
  Array.isArray(p) ? p.map((x) => String(x).toLowerCase().replace(/[^a-z0-9]/g, '')).filter((x): x is MusicTag => (TAGS as string[]).includes(x))
    : p === 'flirt' ? ['k18'] : ['friends', 'meet', 'date'];

/** does this song belong in this room? */
export const playsIn = (t: Track, r: MusicRoom): boolean =>
  t.pool.includes('all') || t.pool.includes(r.kind) || (r.k18 && t.pool.includes('k18'));

/** A song and everything the director needs to place it. Sample positions are on the DECODED asset. */
type Song = Omit<Track, 'id' | 'pool'> & { pool: unknown };

/**
 * The songs that exist, by slot — read from apps/client/public/audio/music/tracks.json, which is THE source: the
 * game imports it and /music.html fetches it, so the test page and the game can never disagree about a loop point.
 * Only songs that exist are in the list: the random pick draws from real songs, so with two general songs a room hears
 * them in turn, and nothing is stored or listed ten times.
 */
const SONGS = REGISTRY.songs as unknown as Record<string, Song>;

export const TRACKS: Track[] = Object.keys(SONGS).sort((a, b) => Number(a) - Number(b))
  .map((slot) => ({ id: `music${slot}`, ...SONGS[slot]!, pool: poolOf((SONGS[slot] as unknown as { pool: unknown }).pool) }));
